import React, { useState, useRef, useEffect, memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchResult } from '../types';

interface SearchProps {
  onSearch: (query: string) => void;
  searchResults: SearchResult[];
  isSearching: boolean;
}

const Search: React.FC<SearchProps> = memo(({ onSearch, searchResults, isSearching }) => {
  const [query, setQuery] = useState('');
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (inputRef.current && !inputRef.current.contains(target) &&
          resultsRef.current && !resultsRef.current.contains(target)) {
        setIsResultsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    const trimmedValue = value.trim();

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (trimmedValue.length === 0) {
      setIsResultsOpen(false);
      return;
    }

    if (trimmedValue.length < 2) {
      setIsResultsOpen(true);
      return;
    }

    // Debounce search
    const timeout = setTimeout(() => {
      onSearch(trimmedValue);
      setIsResultsOpen(true);
    }, 300);

    setSearchTimeout(timeout);
  }, [onSearch, searchTimeout]);

  const handleFocus = useCallback(() => {
    if (query.length > 0) {
      setIsResultsOpen(true);
    }
  }, [query.length]);

  const handleBlur = useCallback(() => {
    // Delay hiding results to allow clicking on results
    setTimeout(() => {
      if (resultsRef.current && !resultsRef.current.contains(document.activeElement)) {
        setIsResultsOpen(false);
      }
    }, 150);
  }, []);

  const getSearchTypeLabel = useCallback((type: string): string => {
    const labels: Record<string, string> = {
      'artist': 'מציג.ה',
      'artwork': 'יצירה',
      'exhibition': 'תערוכה'
    };
    return labels[type] || type;
  }, []);

  const handleResultClick = useCallback((result: SearchResult, e: React.MouseEvent) => {
    e.preventDefault();
    setIsResultsOpen(false);
    setQuery('');

    // Scroll to top when navigating from search
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (result.type === 'artist') {
      // Use artist slug if available and valid, otherwise use ID
      const targetSlug = (result.artist_slug && result.artist_slug.trim()) ? result.artist_slug : result.id;

      if (targetSlug) {
        const encodedSlug = encodeURIComponent(targetSlug);
        navigate(`/artist/${encodedSlug}`);
      }
    } else if (result.type === 'artwork') {
      // Navigate to artist page if available
      if (result.artist_slug && result.artist_slug.trim()) {
        navigate(`/artist/${encodeURIComponent(result.artist_slug)}`);
      }
    } else if (result.type === 'exhibition') {
      navigate('/exhibition');
    }
  }, [navigate]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && query.trim().length >= 2) {
      setIsResultsOpen(false);
      // Scroll to top when navigating from search
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  }, [query, navigate]);

  const renderSearchResults = () => {
    if (query.length === 0) return null;

    if (query.length < 2) {
      return (
        <div className="search-result-item">
          <div className="search-result-title">הכנס לפחות 2 תווים לחיפוש</div>
        </div>
      );
    }

    if (isSearching) {
      return (
        <div className="search-result-item">
          <div className="search-result-title">מחפש...</div>
        </div>
      );
    }

    if (searchResults.length === 0) {
      return (
        <div className="search-result-item">
          <div className="search-result-title">לא נמצאו תוצאות</div>
        </div>
      );
    }

    const resultItems = searchResults.slice(0, 5).map((result, index) => (
      <button 
        key={index} 
        className="search-result-item"
        onClick={(e) => handleResultClick(result, e)}
        type="button"
      >
        <div className="search-result-title">{result.title}</div>
        <div className="search-result-type">{getSearchTypeLabel(result.type)}</div>
      </button>
    ));

    // Add "See all results" button if there are more than 5 results
    if (searchResults.length > 5) {
      resultItems.push(
        <button
          key="see-all"
          className="search-result-item search-see-all"
          onClick={() => {
            setIsResultsOpen(false);
            // Scroll to top when navigating from search
            window.scrollTo({ top: 0, behavior: 'smooth' });
            navigate(`/search?q=${encodeURIComponent(query)}`);
          }}
          type="button"
        >
          <div className="search-result-title">
            צפה בכל התוצאות ({searchResults.length})
          </div>
        </button>
      );
    }

    return resultItems;
  };

  return (
    <div className="search-container">
      <div className="search-bar">
        <svg 
          className="search-icon" 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
        </svg>
        <input 
          type="text" 
          placeholder="" 
          className="search-input"
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          ref={inputRef}
          aria-label="חיפוש באתר"
          autoComplete="off"
        />
      </div>
      <div 
        className={`search-results ${isResultsOpen ? 'show' : ''}`}
        ref={resultsRef}
        role="listbox"
        aria-label="תוצאות חיפוש"
      >
        {renderSearchResults()}
      </div>
    </div>
  );
});

Search.displayName = 'Search';

export default Search;